import { SiteLogo } from "@/components/shared/SiteLogo";
import { DEALER_SEO_LINKS, DEALER_EXTRA_LINKS } from "./DealerCtaStrip";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, User, LogOut, LayoutDashboard, Shield, Phone } from "lucide-react";
import { getPageContent } from "@/lib/api";

const DEFAULT_NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Our Process", path: "/our-process" },
  { name: "Locations", path: "/locations" },
  { name: "Blog", path: "/blog" },
  { name: "Gallery", path: "/gallery" },
  { name: "Careers", path: "/careers" },
  { name: "FAQs", path: "/faqs" },
  { name: "Become a Dealer", path: "/become-dealer" },
  { name: "Contact", path: "/contact" },
];

const DEFAULT_PHONE = { label: "(248) 590-0000", href: "tel:+12485900000" };

const DEFAULT_FOOTER = {
  brand: "Your trusted Total Wireless Master Agent. Empowering dealers nationwide.",
  quickLinks: [
    { name: "About Us", path: "/about-us" },
    { name: "Services", path: "/services" },
    { name: "Our Process", path: "/our-process" },
    { name: "FAQs", path: "/faqs" },
  ],
  usefulLinks: [
    { name: "Locations", path: "/locations" },
    { name: "Blog", path: "/blog" },
    { name: "Gallery", path: "/gallery" },
    { name: "Careers", path: "/careers" },
    { name: "Become a Dealer", path: "/become-dealer" },
    { name: "Open a Store", path: "/open-a-store" },
  ],
  contactItems: ["info@primemediawireless.com", "1-800-PRIME-MW"],
};

// Shared CMS cache so Navbar & Footer don't both re-fetch
let _cmsCache = { header: null, footer: null };

function getSection(sections, name) {
  return sections.find((s) => s.section_name === name && s.active !== false);
}

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const [navLinks, setNavLinks] = useState(DEFAULT_NAV_LINKS);
  const [phone, setPhone] = useState(DEFAULT_PHONE);

  useEffect(() => {
    if (_cmsCache.header) {
      applyHeader(_cmsCache.header);
      return;
    }
    getPageContent("header").then((sections) => {
      _cmsCache.header = sections;
      applyHeader(sections);
    }).catch(() => {});
  }, []);

  const applyHeader = (sections) => {
    const navSection = getSection(sections, "nav_links");
    if (navSection?.metadata?.items?.length) {
      setNavLinks(navSection.metadata.items.map((i) => ({ name: i.title, path: i.description })));
    }
    const phoneSection = getSection(sections, "phone");
    if (phoneSection) {
      setPhone({ label: phoneSection.content || DEFAULT_PHONE.label, href: phoneSection.button_link || DEFAULT_PHONE.href });
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <SiteLogo className="h-14 w-auto" testId="navbar-logo" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 rounded-md font-body text-sm whitespace-nowrap transition-colors ${
                  isActive(link.path)
                    ? "bg-teal/10 text-teal font-medium"
                    : "text-slate-600 hover:text-navy hover:bg-slate-100"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Auth Buttons / User Menu */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-teal/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-teal" />
                    </div>
                    <span className="font-medium text-slate-700">{user.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2" data-testid="admin-portal-link">
                        <Shield className="w-4 h-4" />
                        Admin Portal
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(user.role === "dealer" || user.role === "admin") && (
                    <DropdownMenuItem asChild>
                      <Link to="/dealer" className="flex items-center gap-2" data-testid="dealer-portal-link">
                        <LayoutDashboard className="w-4 h-4" />
                        Dealer Portal
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">Login</Button>
                </Link>
                <a href={phone.href}>
                  <Button className="bg-teal hover:bg-teal-dark text-white" data-testid="click-to-call-btn">
                    <Phone className="w-4 h-4 mr-1.5" /> {phone.label}
                  </Button>
                </a>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-md font-body ${
                    isActive(link.path)
                      ? "bg-teal/10 text-teal font-medium"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-slate-200 mt-2 pt-2">
                {user ? (
                  <>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-md"
                      >
                        Admin Portal
                      </Link>
                    )}
                    {(user.role === "dealer" || user.role === "admin") && (
                      <Link
                        to="/dealer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-md"
                      >
                        Dealer Portal
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-md"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-slate-600 hover:bg-slate-100 rounded-md"
                    >
                      Login
                    </Link>
                    <a
                      href={phone.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-1.5 px-4 py-3 text-teal font-medium hover:bg-teal/10 rounded-md"
                    >
                      <Phone className="w-4 h-4" /> {phone.label}
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export const Footer = () => {
  const [footerData, setFooterData] = useState(null);

  useEffect(() => {
    if (_cmsCache.footer) {
      setFooterData(_cmsCache.footer);
      return;
    }
    getPageContent("footer").then((sections) => {
      _cmsCache.footer = sections;
      setFooterData(sections);
    }).catch(() => {});
  }, []);

  const brandSection = footerData ? getSection(footerData, "brand") : null;
  const quickLinksSection = footerData ? getSection(footerData, "quick_links") : null;
  const usefulLinksSection = footerData ? getSection(footerData, "useful_links") : null;
  const contactSection = footerData ? getSection(footerData, "contact_info") : null;

  const brandText = brandSection?.content || DEFAULT_FOOTER.brand;
  const quickLinksTitle = quickLinksSection?.title || "Quick Links";
  const quickLinks = quickLinksSection?.metadata?.items?.length
    ? quickLinksSection.metadata.items.map((i) => ({ name: i.title, path: i.description }))
    : DEFAULT_FOOTER.quickLinks;
  const usefulLinksTitle = usefulLinksSection?.title || "Useful Links";
  const usefulLinksBase = usefulLinksSection?.metadata?.items?.length
    ? usefulLinksSection.metadata.items.map((i) => ({ name: i.title, path: i.description }))
    : DEFAULT_FOOTER.usefulLinks;
  const usefulLinks = [...usefulLinksBase, ...[...DEALER_SEO_LINKS, ...DEALER_EXTRA_LINKS].filter((s) => !usefulLinksBase.some((u) => u.name.toLowerCase() === s.name.toLowerCase()))];
  const contactTitle = contactSection?.title || "Contact Us";
  const contactItems = contactSection?.metadata?.items?.length
    ? contactSection.metadata.items.map((i) => i.title)
    : DEFAULT_FOOTER.contactItems;

  return (
    <footer className="bg-navy text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 -mt-8">
            <div className="flex items-center gap-2 mb-0">
              <SiteLogo className="h-24 w-auto" style={{ filter: "brightness(0) invert(1) sepia(1) saturate(5) hue-rotate(130deg) brightness(0.85)" }} testId="footer-logo" />
            </div>
            <p className="text-slate-400 text-sm font-body">{brandText}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">{quickLinksTitle}</h4>
            <ul className="space-y-2">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  <Link to={item.path} className="text-slate-400 hover:text-teal transition-colors text-sm font-body">{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Useful Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">{usefulLinksTitle}</h4>
            <ul className="space-y-2">
              {usefulLinks.map((item) => (
                <li key={item.name}>
                  <Link to={item.path} className="text-slate-400 hover:text-teal transition-colors text-sm font-body">{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">{contactTitle}</h4>
            <ul className="space-y-2 text-slate-400 text-sm font-body">
              {contactItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm font-body">
            &copy; {new Date().getFullYear()} Prime Media Wireless. All rights reserved.
          </p>
          <div className="flex gap-4 text-slate-500 text-sm font-body">
            <Link to="/privacy" className="hover:text-teal transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-teal transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
